import DatePicker from 'components/ui/Form/DatePicker/DatePicker';
import Form, {FormOnSubmit, WithFormError} from 'components/ui/Form/Form/Form';
import TextInput from 'components/ui/Form/TextInput/TextInput';
import React, {useMemo} from 'react';
import AmountInput from 'components/ui/Form/AmountInput/AmountInput';
import {useFieldArray, useForm} from 'react-hook-form';
import Button from 'components/ui/Button';
import IconSVG from 'components/ui/Icon/IconSVG';
import {
    useGetExpenseCategories,
    useGetExpenseCostCenters,
    useGetExpenseVats,
} from 'api/expenses';
import AutoComplete from 'components/ui/Form/AutoComplete/AutoComplete';
import TextArea from 'components/ui/Form/TexArea/TextArea';
import {
    ICardTransactionDetail,
    ICardTransactionDetailsPublic,
} from 'types/transactions';
import {formatDateInZurichTZ} from 'helpers/dates';
import useModal from 'components/ui/ModalContainer/useModal';
import {handleTypedFormError} from 'helpers/handleFormError';
import {yupResolver} from '@hookform/resolvers';
import {expenseFormValidationSchema} from 'form/validation/expenses';
import NumberInput from 'components/ui/Form/NumberInput/NumberInput';
import ConfirmationOverlay from 'components/ui/Overlays/ConfirmationOverlay/ConfirmationOverlay';
import {isCardTransactionDetail} from 'helpers/transactions';
import {
    ExpenseConfiguration,
    UpdateExpenseOnCardTransactionDetails,
} from 'types/api/expenses';
import {UseMutateFunction} from '@tanstack/react-query';
import {HashParamType} from 'types/common';
import {useHistory, useLocation, useParams} from 'react-router';
import {useBusinessPartner} from 'context/business-partner-context';
import Alert from 'components/ui/Alert/Alert';
import {PATH} from 'constants/common';
import {useUpdateMerchant} from 'api/merchants';
import Checkbox from 'components/ui/Form/Checkbox/Checkbox';
import styles from './ExpenseForm.module.scss';
import VatRateAutoComplete from 'components/ui/Form/VatRateAutoComplete/VatRateAutoComplete';
import {formatNumber} from 'helpers/common';

export type UpdateExpenseMethods = {
    isLoading: boolean;
    updateExpense: UseMutateFunction<
        unknown,
        unknown,
        UpdateExpenseOnCardTransactionDetails,
        unknown
    >;
};

interface IProps {
    cardTransactionDetail:
        | ICardTransactionDetail
        | ICardTransactionDetailsPublic;
    submitText?: string;
    onSubmit?: () => void;
    updateExpenseMethods: UpdateExpenseMethods;
    expenseManagementConfiguration?: Partial<ExpenseConfiguration>;
}

interface IFormValues {
    vat: ICardTransactionDetail['vat'];
    amount: number;
    expenseCategory: string;
    expenseCostCenter: string;
    currency: string;
    merchant: string;
    executionDate: string;
    comment: string;
    saveAccounting: boolean;
}

const ExpenseForm = ({
    cardTransactionDetail,
    submitText = 'Save and close',
    onSubmit,
    updateExpenseMethods,
    expenseManagementConfiguration,
}: IProps) => {
    const history = useHistory();
    const {activeBusinessPartner} = useBusinessPartner();
    const {hash} = useParams<HashParamType>();
    const {openModalWithContent, closeModal} = useModal();
    const {pathname} = useLocation();
    const {data: categories, isLoading: isCategoriesLoading} =
        useGetExpenseCategories(
            !isCardTransactionDetail(cardTransactionDetail),
        );
    const {data: costCenters, isLoading: isCostCentersLoading} =
        useGetExpenseCostCenters(
            !isCardTransactionDetail(cardTransactionDetail),
        );
    const {updateExpense, isLoading} = updateExpenseMethods;
    const {mutate: updateMerchant, isPending: isUpdateMerchantLoading} =
        useUpdateMerchant();

    const {data: vatRates} = useGetExpenseVats(
        expenseManagementConfiguration?.vatRequired &&
            isCardTransactionDetail(cardTransactionDetail),
    );

    const formMethods = useForm<WithFormError<IFormValues>>({
        defaultValues: {
            vat: cardTransactionDetail?.vat?.length
                ? cardTransactionDetail?.vat
                : [{amount: '', rate: '', expenseVat: null, grossAmount: ''}],
            amount: Math.abs(cardTransactionDetail?.amount),
            expenseCategory: cardTransactionDetail?.expenseCategory?.['@id'],
            expenseCostCenter:
                cardTransactionDetail?.expenseCostCenter?.['@id'],
            currency: cardTransactionDetail?.currency?.split('/').pop(),
            merchant: cardTransactionDetail?.merchant?.name,
            executionDate: formatDateInZurichTZ(
                cardTransactionDetail?.executionDate,
            ),
            comment: cardTransactionDetail?.comment,
            saveAccounting: false,
        },
        resolver: yupResolver(expenseFormValidationSchema),
    });
    const {control, setValue, getValues, watch} = formMethods;

    const {fields, remove, append} = useFieldArray<{
        rate: string;
        amount: string;
        currency: string;
        grossAmount: string;
        expenseVat?: string;
    }>({
        control,
        name: 'vat',
    });

    const handleSubmit: FormOnSubmit<IFormValues> = (values, setError) => {
        if (values.saveAccounting) {
            handleSaveAutoAccounting(values, setError);
        } else {
            handleUpdateExpense(values, setError);
        }
    };

    const handleSaveAutoAccounting: FormOnSubmit<IFormValues> = (
        values,
        setError,
    ) => {
        updateMerchant(
            {
                '@id': cardTransactionDetail.merchant['@id'],
                expenseCategory: values.expenseCategory,
                expenseCostCenter: values.expenseCostCenter,
                vat: values.vat?.[0] ? +values.vat?.[0].rate : null,
                tags: [],
            },
            {
                onSuccess: () => handleUpdateExpense(values, setError),
            },
        );
    };

    const handleUpdateExpense: FormOnSubmit<IFormValues> = (
        values,
        setError,
    ) => {
        const {expenseCategory, vat, comment, expenseCostCenter} = values;

        const vatOrNull =
            vat?.filter(v => v.amount !== '' && v.rate !== '') || null;

        updateExpense(
            {
                '@id': cardTransactionDetail['@id'],
                hash,
                expenseCategory,
                expenseCostCenter,
                vat: vatOrNull,
                comment,
            },
            {
                onSuccess: () => {
                    closeModal('expense');
                    if (onSubmit) onSubmit();
                },
                onError: (errors: any) =>
                    handleTypedFormError(errors, setError, values),
            },
        );
    };

    const handleDelete = (index: number) => {
        openModalWithContent(
            'confirmation',
            <ConfirmationOverlay
                title="Delete VAT rate?"
                description="This action cannot be reversed."
                onConfirm={() => {
                    remove(index);
                    closeModal('confirmation');
                }}
                confirmVariant="error"
                confirmLabel="Delete"
                onCancel={() => closeModal('confirmation')}
            />,
        );
    };

    const handleVatRateChange = (value: string, index: number) => {
        const {vat: vatFields} = getValues();
        if (!cardTransactionDetail || Number.isNaN(value)) return;

        if (vatFields[index].grossAmount && value) {
            setValue(
                `vat.${index}.amount`,
                (
                    +vatFields[index].grossAmount -
                    +vatFields[index].grossAmount / (1 + +value / 100)
                ).toFixed(2),
            );
        }
    };

    const costCenterOptions = useMemo(() => {
        return (
            costCenters ||
            (cardTransactionDetail as ICardTransactionDetailsPublic)
                ?.expenseCostCenters ||
            []
        ).map(costCenter => ({
            name: `${costCenter.name} - ${costCenter.number}`,
            '@id': costCenter['@id'],
        }));
    }, [cardTransactionDetail, costCenters]);

    const categoriesOptions = useMemo(() => {
        return (
            categories ||
            (cardTransactionDetail as ICardTransactionDetailsPublic)
                ?.expenseCategories ||
            []
        );
    }, [cardTransactionDetail, categories]);

    const vatRatesOptions = useMemo(() => {
        return (
            vatRates ||
            (cardTransactionDetail as ICardTransactionDetailsPublic)
                ?.expenseVATs ||
            []
        );
    }, [cardTransactionDetail, vatRates]);

    const vatValues = watch('vat');

    const inconsistentGrossValues = useMemo(() => {
        return (
            vatValues.reduce(
                (acc, curr) => acc + Number(curr.grossAmount),
                0,
            ) !== Math.abs(cardTransactionDetail.amount)
        );
    }, [vatValues, cardTransactionDetail]);

    const finalExternalIntegration =
        cardTransactionDetail.externalIntegrations?.find(
            e => e.type === 'final',
        );

    return (
        <Form formMethods={formMethods} onSubmit={handleSubmit}>
            <TextInput name="merchant" label="Merchant" disabled />
            <DatePicker name="executionDate" label="Date" disabled />
            <div className={styles.formRow}>
                <AmountInput name="amount" label="Amount" disabled />
                <TextInput
                    className={styles.currency}
                    name="currency"
                    label="Currency"
                    disabled
                />
            </div>
            {expenseManagementConfiguration?.vatRequired ? (
                <>
                    {fields.map((field, index) => {
                        return (
                            <div className={styles.formRow} key={field.id}>
                                <NumberInput
                                    label="Gross amount"
                                    name={`vat.${index}.grossAmount`}
                                    defaultValue={
                                        field.grossAmount ||
                                        (index === 0 &&
                                            `${Math.abs(cardTransactionDetail.amount)}`) ||
                                        ''
                                    }
                                    containerClassName={styles.vatFields}
                                    disabled={!!finalExternalIntegration}
                                />
                                {vatRatesOptions?.length ? (
                                    <VatRateAutoComplete
                                        name={`vat.${index}.rate`}
                                        options={vatRatesOptions}
                                        onChange={v => {
                                            setValue(
                                                `vat.${index}.expenseVat`,
                                                v['@id'],
                                            );
                                            handleVatRateChange(
                                                v.value.toString(),
                                                index,
                                            );
                                        }}
                                        disabled={!!finalExternalIntegration}
                                    />
                                ) : (
                                    <NumberInput
                                        label="VAT rate"
                                        name={`vat.${index}.rate`}
                                        defaultValue={field.rate}
                                        containerClassName={styles.vatFields}
                                        endAdornment="%"
                                        placeholder={formatNumber(1.23)}
                                        onBlur={e =>
                                            handleVatRateChange(
                                                e.target.value,
                                                index,
                                            )
                                        }
                                        disabled={!!finalExternalIntegration}
                                    />
                                )}
                                <NumberInput
                                    label="VAT amount"
                                    name={`vat.${index}.amount`}
                                    placeholder={formatNumber(1234)}
                                    defaultValue={field.amount}
                                    containerClassName={styles.vatFields}
                                    disabled={!!finalExternalIntegration}
                                />
                                <TextInput
                                    name={`vat.${index}.expenseVat`}
                                    defaultValue={field.expenseVat}
                                    hidden
                                />
                                <Button
                                    iconOnly
                                    variant="text"
                                    icon={<IconSVG name="trash" />}
                                    onClick={() => handleDelete(index)}
                                    className={styles.deleteIcon}
                                    disabled={!!finalExternalIntegration}
                                />
                            </div>
                        );
                    })}
                </>
            ) : null}
            {expenseManagementConfiguration?.vatRequired &&
            !finalExternalIntegration ? (
                <>
                    <Button
                        variant="text"
                        onClick={() =>
                            append({
                                amount: '',
                                currency: cardTransactionDetail?.currency
                                    ?.split('/')
                                    .pop(),
                                rate: '',
                            })
                        }
                        icon={<IconSVG name="plus" />}
                    >
                        Add additional VAT rate
                    </Button>
                    {inconsistentGrossValues ? (
                        <Alert
                            variant="warning"
                            message="The total of all gross amounts for the VAT rates does not match the total transaction amount."
                        />
                    ) : null}
                    {(pathname.includes(PATH.EXPENSES_PUBLIC) ||
                        !activeBusinessPartner?.permission?.cardAdmin) &&
                    (cardTransactionDetail?.expenseCategory ||
                        cardTransactionDetail?.expenseCostCenter) ? (
                        <Alert
                            variant="info"
                            message="Auto accounting is live for this merchant."
                        />
                    ) : null}
                </>
            ) : null}
            {activeBusinessPartner?.permission?.cardAdmin &&
            !pathname.includes(PATH.EXPENSES_PUBLIC) &&
            !finalExternalIntegration ? (
                <Alert
                    variant="info"
                    message={
                        <div data-isolate>
                            {cardTransactionDetail?.merchant?.expenseCategory ||
                            cardTransactionDetail?.merchant?.expenseCostCenter
                                ? 'Auto accounting is live for this merchant. You can edit it'
                                : 'You can setup auto accounting for this merchant'}
                            &nbsp;
                            <Button
                                type="button"
                                variant="text"
                                onClick={() => {
                                    history.push(
                                        `${
                                            PATH.MERCHANT
                                        }/${cardTransactionDetail.merchant[
                                            '@id'
                                        ]
                                            .split('/')
                                            .pop()}`,
                                    );
                                    closeModal();
                                }}
                            >
                                here.
                            </Button>
                        </div>
                    }
                />
            ) : null}
            {!!finalExternalIntegration ? (
                <Alert
                    variant="info"
                    message={
                        <div data-isolate>
                            The transaction is already in bexio and changes must
                            be&nbsp;
                            {!pathname.includes(PATH.EXPENSES_PUBLIC) ? (
                                <a
                                    href={`https://office.bexio.com/index.php/accounting/manualEntries/id/${finalExternalIntegration.externalId}`}
                                    target="_blank"
                                >
                                    made there
                                </a>
                            ) : (
                                'made there'
                            )}
                        </div>
                    }
                />
            ) : null}
            {!expenseManagementConfiguration?.categoryRequired ? null : (
                <AutoComplete
                    name="expenseCategory"
                    label="Category"
                    options={categoriesOptions}
                    labelKey="name"
                    isLoading={isCategoriesLoading}
                    isClearable
                    disabled={!!finalExternalIntegration}
                />
            )}
            {!expenseManagementConfiguration?.costCenterRequired ? null : (
                <AutoComplete
                    name="expenseCostCenter"
                    label="Cost center"
                    options={costCenterOptions}
                    labelKey="name"
                    isLoading={isCostCentersLoading}
                    isClearable
                />
            )}
            <TextArea name="comment" label="Note" />
            {activeBusinessPartner?.permission?.cardAdmin &&
            !pathname.includes(PATH.EXPENSES_PUBLIC) &&
            !cardTransactionDetail?.merchant?.expenseCategory &&
            !cardTransactionDetail?.merchant?.expenseCostCenter ? (
                <Checkbox
                    name="saveAccounting"
                    afterLabel={
                        <span>
                            Save this category, cost center and VAT for future
                            transactions with{' '}
                            <var data-var="merchant-name">
                                {cardTransactionDetail.merchant.name}
                            </var>
                            .
                        </span>
                    }
                />
            ) : null}
            <Button
                type="submit"
                isLoading={isLoading || isUpdateMerchantLoading}
                className={styles.submit}
            >
                {submitText}
            </Button>
        </Form>
    );
};

export default ExpenseForm;
